import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { AdminPageWrapper } from "../AdminPageWrapper";
import {
  HStack,
  Stack,
  Text,
  Table,
  Spinner,
  Button,
  useDisclosure,
  Icon,
} from "@chakra-ui/react";
import { AdminView } from "../../models/AdminView";
import { PageControls } from "../../../pages/map/views/BottomBar/osa-way/PageControls";
import { Page } from "../../../shared/utils/http-utils";
import { useStores } from "../../../shared/stores/RootStore";
import { AdminCreateAccountModal } from "./AdminCreateAccountModal";
import { AdminAccountApi } from "../../services/AdminAccountsApi";
import { appRoutes } from "../../../../App";
import { Link } from "react-router-dom";
import { ExternalLinkIcon } from "../../../shared/views/icons";
import { AdminAccount } from "../../models/AdminAccount";

const pageSize = 20;

const AdminAccountsPageBase = () => {
  const { accountStore } = useStores();
  const accountUser = accountStore.selectedUserAccount;

  const createAccountModal = useDisclosure();

  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<Page<AdminAccount>>();

  const goToPage = async (pageNumber: number) => {
    setLoading(true);
    const accountsPage = await AdminAccountApi.page(pageSize, pageNumber).then(
      (res) => res.data
    );
    setPage(accountsPage);
    setLoading(false);
  };

  useEffect(() => {
    if (!accountUser) return;
    goToPage(pageIndex);
    // eslint-disable-next-line
  }, [accountUser, pageIndex]);

  return (
    <AdminPageWrapper selectedView={AdminView.Accounts}>
      <Stack px={6}>
        <Text fontSize="2xl">{AdminView.Accounts}</Text>

        <HStack justifyContent={"end"}>
          <PageControls
            pageSize={pageSize}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            nItems={page?.meta?.totalElements ?? 0}
          />
        </HStack>

        {loading ? (
          <Spinner color={"primary.500"} />
        ) : (
          <Table.Root striped>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Admin email</Table.ColumnHeader>
                <Table.ColumnHeader>Created at</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {(page?.data ?? [])
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                )
                .map((account) => (
                  <Table.Row key={account.id}>
                    <Table.Cell>
                      <Link to={appRoutes.account(account.id)}>
                        <HStack _hover={{ textDecor: "underline" }}>
                          <Text>{account.name}</Text>
                          <Icon as={ExternalLinkIcon} />
                        </HStack>
                      </Link>
                    </Table.Cell>
                    <Table.Cell>{account.email}</Table.Cell>
                    <Table.Cell>
                      {new Date(account.createdAt).toLocaleDateString()}
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table.Root>
        )}

        <Button
          colorPalette="primary"
          alignSelf="flex-end"
          onClick={createAccountModal.onOpen}
        >
          Create new account
        </Button>

        {createAccountModal.open && (
          <AdminCreateAccountModal
            onClose={createAccountModal.onClose}
            onCreated={() => goToPage(pageIndex)}
          />
        )}
      </Stack>
    </AdminPageWrapper>
  );
};

export const AdminAccountsPage = observer(AdminAccountsPageBase);
